import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { canEditItems } from "@/lib/roles";
import { DownloadSimple } from "@phosphor-icons/react";

const DIET_TYPES = ["Wellness", "Vegetarian", "Bland", "ESR", "CKD3", "Pescatarian", "GDM", "Well+"];

// Download a role-gated export by streaming it through the axios client so the
// Authorization header travels with the request. Preserves the server's
// filename via Content-Disposition when present.
async function downloadExport(url, fallbackName) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = /filename=([^;]+)/i.exec(cd);
    const name = (m ? m[1].replace(/^["']|["']$/g, "").trim() : fallbackName) || fallbackName;
    const blob = new Blob([res.data]);
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 0);
  } catch (e) {
    toast.error(formatApiError(e));
  }
}

export default function LiveInventoryContent() {
  const { user } = useAuth();
  const canExport = canEditItems(user); // Admin + Supervisor only
  const [tab, setTab] = useState("meals");
  const [meals, setMeals] = useState([]);
  const [items, setItems] = useState([]);
  const [mealFilter, setMealFilter] = useState({ diet_type: "", cycle_week: "" });
  const [dryFilter, setDryFilter] = useState({ q: "", category: "" });

  useEffect(() => {
    if (tab === "meals") {
      const params = new URLSearchParams();
      if (mealFilter.diet_type) params.set("diet_type", mealFilter.diet_type);
      if (mealFilter.cycle_week) params.set("cycle_week", mealFilter.cycle_week);
      params.set("limit", "500");
      api.get(`/meals?${params.toString()}`).then((r) => setMeals(r.data)).catch(() => {});
    } else {
      const params = new URLSearchParams();
      if (dryFilter.q) params.set("q", dryFilter.q);
      if (dryFilter.category) params.set("category", dryFilter.category);
      params.set("limit", "500");
      api.get(`/items?${params.toString()}`).then((r) => setItems(r.data)).catch(() => {});
    }
  }, [tab, mealFilter, dryFilter]);

  const dryCategories = Array.from(new Set(items.map((v) => v.category).filter(Boolean))).sort();

  const statusOf = (m) => {
    const c = Number(m.current_meals || 0);
    const par = Number(m.par_level || 0);
    if (c <= 0) return { label: "OUT", tone: "text-destructive" };
    if (c < par) return { label: "LOW", tone: "text-yellow-500" };
    return { label: "OK", tone: "text-success" };
  };

  const mealsExportUrl = (fmt) => {
    const p = new URLSearchParams({ fmt });
    if (mealFilter.diet_type) p.set("diet_type", mealFilter.diet_type);
    if (mealFilter.cycle_week) p.set("cycle_week", mealFilter.cycle_week);
    return `/io/export/live-inventory/meals?${p.toString()}`;
  };
  const dryExportUrl = (fmt) => {
    const p = new URLSearchParams({ fmt });
    if (dryFilter.q) p.set("q", dryFilter.q);
    if (dryFilter.category) p.set("category", dryFilter.category);
    return `/io/export/live-inventory/dry-goods?${p.toString()}`;
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button
          data-testid="live-inv-tab-meals"
          onClick={() => setTab("meals")}
          className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "meals" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
        >Meals</button>
        <button
          data-testid="live-inv-tab-dry"
          onClick={() => setTab("dry")}
          className={`h-8 px-3 border rounded-sm text-[10px] font-bold uppercase tracking-widest ${tab === "dry" ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
        >Dry Goods</button>
        {canExport && (
          <div className="ml-auto flex items-center gap-1">
            <button
              data-testid={tab === "meals" ? "live-inv-export-meals-xlsx" : "live-inv-export-dry-xlsx"}
              onClick={() => downloadExport(
                tab === "meals" ? mealsExportUrl("xlsx") : dryExportUrl("xlsx"),
                tab === "meals" ? "live_inventory_meals.xlsx" : "live_inventory_dry_goods.xlsx",
              )}
              className="btn-hard flex items-center gap-1 h-8 px-2 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-[10px] rounded-sm hover:bg-orange-600 hover:text-white"
            >
              <DownloadSimple size={12} weight="bold" /> Excel
            </button>
            <button
              data-testid={tab === "meals" ? "live-inv-export-meals-csv" : "live-inv-export-dry-csv"}
              onClick={() => downloadExport(
                tab === "meals" ? mealsExportUrl("csv") : dryExportUrl("csv"),
                tab === "meals" ? "live_inventory_meals.csv" : "live_inventory_dry_goods.csv",
              )}
              className="btn-hard flex items-center gap-1 h-8 px-2 border border-border text-foreground uppercase tracking-widest text-[10px] font-bold rounded-sm hover:bg-muted"
            >
              <DownloadSimple size={12} weight="bold" /> CSV
            </button>
          </div>
        )}
      </div>

      {tab === "meals" ? (
        <>
          <div className="flex gap-2 mb-2 flex-wrap">
            <select
              data-testid="live-inv-meals-diet-type"
              value={mealFilter.diet_type}
              onChange={(e) => setMealFilter({ ...mealFilter, diet_type: e.target.value })}
              className="h-8 px-2 border border-border bg-background rounded-sm text-xs"
            >
              <option value="">All diet types</option>
              {DIET_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
            </select>
            <select
              data-testid="live-inv-meals-cycle-week"
              value={mealFilter.cycle_week}
              onChange={(e) => setMealFilter({ ...mealFilter, cycle_week: e.target.value })}
              className="h-8 px-2 border border-border bg-background rounded-sm text-xs"
            >
              <option value="">All weeks</option>
              {[1, 2, 3, 4, 5].map((w) => (<option key={w} value={w}>Week {w}</option>))}
            </select>
            <span className="ml-auto text-xs font-mono text-muted-foreground">{meals.length} rows</span>
          </div>
          <div className="max-h-[400px] overflow-auto border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
                <tr className="text-left label-tech">
                  <th className="px-2 py-1.5">Diet Type</th>
                  <th className="px-2 py-1.5">Diet Code</th>
                  <th className="px-2 py-1.5 text-center">Cycle Wk</th>
                  <th className="px-2 py-1.5 text-center">Pos</th>
                  <th className="px-2 py-1.5 text-right">Meals</th>
                  <th className="px-2 py-1.5">Location</th>
                  <th className="px-2 py-1.5 text-right">Cost</th>
                  <th className="px-2 py-1.5 text-center">Status</th>
                  <th className="px-2 py-1.5">Last Tx</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {meals.map((m) => {
                  const s = statusOf(m);
                  const c = Number(m.current_meals || 0);
                  const lastTx = m.last_transaction_at || m.updated_at;
                  return (
                    <tr key={m.meal_record_id} className={c < m.par_level ? "bg-destructive/5" : ""}>
                      <td className="px-2 py-1">{m.diet_type}</td>
                      <td className="px-2 py-1 font-mono">{m.diet_code}</td>
                      <td className="px-2 py-1 text-center font-mono">{m.cycle_week ?? "—"}</td>
                      <td className="px-2 py-1 text-center font-mono">{m.cycle_position ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-mono font-bold">{c.toFixed(0)}</td>
                      <td className="px-2 py-1 truncate max-w-[16ch]">{m.location || "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">
                        {m.cost === null || m.cost === undefined || m.cost === "" ? "—" : `$${Number(m.cost).toFixed(4)}`}
                      </td>
                      <td className={`px-2 py-1 text-center font-bold text-[10px] ${s.tone}`}>{s.label}</td>
                      <td className="px-2 py-1 text-[10px] text-muted-foreground">{lastTx ? new Date(lastTx).toLocaleDateString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-2 mb-2 flex-wrap">
            <input
              data-testid="live-inv-dry-search"
              value={dryFilter.q}
              onChange={(e) => setDryFilter({ ...dryFilter, q: e.target.value })}
              placeholder="Search Product ID or Name…"
              className="h-8 px-2 border border-border bg-background rounded-sm text-xs w-56"
            />
            <select
              data-testid="live-inv-dry-category"
              value={dryFilter.category}
              onChange={(e) => setDryFilter({ ...dryFilter, category: e.target.value })}
              className="h-8 px-2 border border-border bg-background rounded-sm text-xs"
            >
              <option value="">All categories</option>
              {dryCategories.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
            <span className="ml-auto text-xs font-mono text-muted-foreground">{items.length} rows</span>
          </div>
          <div className="max-h-[400px] overflow-auto border border-border rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-muted/60 border-b border-border sticky top-0 z-10">
                <tr className="text-left label-tech">
                  <th className="px-2 py-1.5">Product Name</th>
                  <th className="px-2 py-1.5">Category</th>
                  <th className="px-2 py-1.5">Pack Size</th>
                  <th className="px-2 py-1.5 text-right">Live Inventory</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((v) => (
                  <tr key={v.variant_id}>
                    <td className="px-2 py-1 truncate max-w-[28ch]">{v.product_name}</td>
                    <td className="px-2 py-1">{v.category || <span className="text-muted-foreground italic">—</span>}</td>
                    <td className="px-2 py-1 font-mono">{v.pack_size || `${v.pack_type} · ${v.units_per_pack} ${v.unit_size}`.trim()}</td>
                    <td className="px-2 py-1 text-right font-mono font-bold">{v.current_qty ?? v.total_qty ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
